import { Backdrop } from '@mui/material';
import { RootState, useAppSelector } from '../../redux/Store';

const SPINNER_SRC = '/gifs/safeview.gif';

/**
 * The shared app-wide page/API loading spinner. Shows while any API request is in
 * flight (pending-request counter from the axios interceptors, see api/http.ts) OR
 * while a page signals a blocking full-page load (pageLoadingMode === 'spinner').
 * Pages drive this one via the loading slice instead of rendering their own
 * full-page loader. Suppressed when a page shows its own loading UI such as the
 * audit skeleton (pageLoadingMode === 'skeleton'), so the two never stack.
 *
 * Note: this is only the shared page/API spinner. Contextual inline loaders still
 * exist for localized UI (e.g. the ViewHistory modal's history body and audit-trail
 * sub-row render their own inline safeview.gif), which is intentional - those are
 * scoped to a modal, not full-page loads.
 */
const GlobalSpinner = () => {
  const { pendingCount, pageLoadingMode } = useAppSelector(
    (state: RootState) => state.loading
  );

  const isLoading =
    pageLoadingMode === 'spinner' ||
    (pendingCount > 0 && pageLoadingMode !== 'skeleton');

  return (
    <Backdrop
      open={isLoading}
      sx={{
        color: '#fff',
        // Opaque white during a full-page load so it hands off seamlessly from the
        // static index.html bootstrap loader (also white) with no flicker. For
        // incremental requests fall back to the translucent default so the spinner
        // dims the already-rendered app instead of blanking it.
        backgroundColor:
          pageLoadingMode === 'spinner' ? '#ffffff' : 'rgba(0, 0, 0, 0.5)',
        zIndex: (theme) => theme.zIndex.modal + 1,
      }}
    >
      <img src={SPINNER_SRC} alt='Loading...' />
    </Backdrop>
  );
};

export default GlobalSpinner;
